import axios from 'axios';

// Determinar URL da API baseado no ambiente
const getApiUrl = () => {
  // Detectar se está em produção (Netlify)
  const isProduction = typeof window !== 'undefined' && (
    window.location.hostname.includes('netlify.app') || 
    window.location.hostname.includes('sindicoon') ||
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  );
  
  // Se estiver em produção, usar URL do Railway
  if (isProduction) {
    // Priorizar variável de ambiente (se foi injetada no build)
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl && envUrl !== 'http://localhost:5000/api') {
      console.log('🔗 Usando URL da API da variável de ambiente:', envUrl);
      return envUrl;
    }
    
    // URL padrão de produção (Railway)
    const productionUrl = 'https://condosmart-production.up.railway.app/api';
    console.log('🌐 Ambiente de produção detectado, usando:', productionUrl);
    return productionUrl;
  }
  
  // Caso contrário, usar localhost (desenvolvimento)
  const devUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  console.log('💻 Ambiente de desenvolvimento, usando:', devUrl);
  return devUrl;
};

const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000
  // Não definir Content-Type aqui - deixar o axios definir automaticamente
  // Para JSON será 'application/json', para FormData será 'multipart/form-data'
});

// Interceptor para adicionar token e condominio_id automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const condominioSalvo = localStorage.getItem('condominioSelecionado');
      if (condominioSalvo) {
        const condominio = JSON.parse(condominioSalvo);
        if (condominio?.condominio_id) {
          config.headers['X-Condominio-Id'] = String(condominio.condominio_id);
        }
      }
    } catch (error) {
      console.warn('Erro ao definir condomínio da requisição:', error);
    }

    const metodo = (config.method || 'get').toLowerCase();

    // Para downloads de arquivos, ajustar headers
    if (config.responseType === 'blob') {
      // Evitar sobrescrever Accept para blobs
      if (!config.headers.Accept) {
        config.headers.Accept = 'text/csv, text/plain, application/octet-stream';
      }
    } else if (!(config.data instanceof FormData) && ['post', 'put', 'patch'].includes(metodo)) {
      // Para FormData, o axios define o Content-Type automaticamente (com boundary)
      // Então não devemos sobrescrevê-lo
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json; charset=utf-8';
      }
      if (!config.headers.Accept) {
        config.headers.Accept = 'application/json; charset=utf-8';
      }
    }
    
    // Lista de rotas que não devem receber condominio_id automaticamente
    const rotasSemCondominioId = [
      '/auth',
      '/condominios',
      '/usuarios/me',
      '/usuarios',  // Listagem de usuários: admin vê todos (sem filtro); condominio_id é enviado pela página quando não for admin
      '/importacao/template'
    ];
    
    // Verificar se a URL atual deve receber condominio_id
    const devePularCondominioId = rotasSemCondominioId.some(rota => 
      config.url.includes(rota)
    );
    
    // Adicionar condominio_id automaticamente às requisições (exceto rotas especiais)
    if (!devePularCondominioId) {
      const condominioSelecionado = localStorage.getItem('condominioSelecionado');
      if (condominioSelecionado) {
        try {
          const condominio = JSON.parse(condominioSelecionado);
          const condominioId = condominio.condominio_id;
          
          if (condominioId) {
            // Para requisições GET, adicionar aos params (só se ainda não existir)
            if (config.method === 'get') {
              // Não adicionar se já existe
              if (!config.params?.condominio_id) {
                config.params = {
                  ...config.params,
                  condominio_id: condominioId
                };
              }
            }
            // Para requisições POST/PUT/PATCH, adicionar ao body (se não for FormData)
            else if (['post', 'put', 'patch'].includes(config.method)) {
              // Se for FormData, adicionar como campo
              if (config.data instanceof FormData) {
                // Só adicionar se ainda não existe
                if (!config.data.has('condominio_id')) {
                  config.data.append('condominio_id', condominioId);
                }
              } 
              // Se for objeto JSON, adicionar ao objeto
              else if (config.data && typeof config.data === 'object') {
                config.data = {
                  ...config.data,
                  condominio_id: condominioId
                };
              }
            }
          }
        } catch (error) {
          console.warn('Erro ao adicionar condominio_id:', error);
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Erro de autenticação
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Erro de conexão com banco (503)
    if (error.response?.status === 503 && 
        error.response?.data?.error === 'DATABASE_CONNECTION_ERROR') {
      
      console.warn('⚠️ Erro de conexão com banco detectado, tentando reconectar...');
      
      // Mostrar notificação para o usuário
      if (window.showNotification) {
        window.showNotification('Conexão perdida, tentando reconectar...', 'warning');
      }
      
      // Tentar reconectar após um delay
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Retry da requisição original
      try {
        const retryConfig = {
          ...error.config,
          timeout: 15000 // Aumentar timeout para retry
        };
        return await api.request(retryConfig);
      } catch (retryError) {
        console.error('❌ Falha ao reconectar:', retryError);
        
        if (window.showNotification) {
          window.showNotification('Falha ao reconectar. Recarregue a página.', 'error');
        }
        
        return Promise.reject(retryError);
      }
    }

    // Erro de timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('⏱️ Timeout na requisição');
      
      if (window.showNotification) {
        window.showNotification('Timeout na requisição. Tentando novamente...', 'warning');
      }
      
      // Retry com timeout maior
      try {
        const retryConfig = {
          ...error.config,
          timeout: 20000
        };
        return await api.request(retryConfig);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

    // Erro de rede
    if (!error.response) {
      console.warn('🌐 Erro de rede detectado');
      
      if (window.showNotification) {
        window.showNotification('Erro de conexão. Verifique sua internet.', 'error');
      }
    }

    return Promise.reject(error);
  }
);

export { api };
export default api;
